/**
 * Partner Dashboard - Dealer-scoped registration view
 *
 * Shown when a user is operating inside a partnership context.
 * Registration-focused, no financials, no cross-dealer data.
 *
 * Clara Vale design system. Cormorant Garamond numbers.
 */

import { useQuery } from '@tanstack/react-query'
import { useOrg } from '@/admin/hooks/use-org'
import { AppShell } from '@/admin/components/app-shell'
import { useNavigate } from 'react-router'
import { ArrowRight, Plus } from 'lucide-react'
import api from '@/admin/lib/api'
import { useFormatting } from '@/admin/hooks/use-formatting'
import { useTranslation } from 'react-i18next'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface PartnerDashboardData {
  dashboard_type: 'partner'
  organisation: { uuid: string; name: string } | null
  summary: {
    total_registrations: number
    registrations_this_month: number
    active_warranties: number
  }
  registrations_by_month: Array<{ month: string; count: number }>
  registrations_by_product: Array<{ name: string; count: number }>
  recent_registrations: Array<{
    uuid: string
    product_name: string
    consumer_email: string
    created_at: string
  }>
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function monthLabel(ym: string): string {
  const [year, month] = ym.split('-')
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
  return `${months[parseInt(month, 10) - 1]} ${year.slice(2)}`
}

function timeAgo(iso: string, locale: string): string {
  const diff = Date.now() - new Date(iso).getTime()
  const mins = Math.floor(diff / 60_000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  if (days < 30) return `${days}d ago`
  return new Date(iso).toLocaleDateString(locale)
}

// ---------------------------------------------------------------------------
// Main
// ---------------------------------------------------------------------------

export function PartnerDashboardPage() {
  const { orgName } = useOrg()
  const navigate = useNavigate()
  const { formatNumber, formatDateLong, locale } = useFormatting()
  const { t } = useTranslation()

  const { data, isPending, isFetching } = useQuery<PartnerDashboardData>({
    queryKey: ['partner-dashboard'],
    queryFn: () => api.get('/business/dashboard/').then((res) => res.data),
  })

  const dealerName = data?.organisation?.name ?? orgName ?? 'there'
  const summary = data?.summary
  const monthlyMax = Math.max(
    ...(data?.registrations_by_month?.map((m) => m.count) ?? []),
    1,
  )

  const today = formatDateLong(new Date().toISOString())

  return (
    <AppShell>
      <main className="page-container py-10">
        {/* ── Page Header ──────────────────────────────────── */}
        <div className="flex items-start justify-between mb-6">
          <div>
            <h1 className="clara-title mb-1">
              {dealerName}
            </h1>
            <p className="text-[13px] text-[var(--ink-3)]">
              {t('partnerDashboard.subtitle', 'Your {{holdingNounLower}} activity')}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => navigate('/registrations/new')}
              className="flex cursor-pointer items-center gap-1.5 rounded-[4px] border-none bg-[var(--green)] px-4 py-2 text-[12px] font-[400] tracking-[0.02em] text-white transition-colors duration-150 hover:bg-[var(--green-hover)]"
            >
              <Plus className="h-3.5 w-3.5" />
              {t('registrations.newRegistration', 'New registration')}
            </button>
            <span className="text-[11px] font-[400] uppercase tracking-[0.1em] text-[var(--ink-3)]">
              {today}
            </span>
          </div>
        </div>

        {/* ── Section 1: Summary Metrics ──────────────────── */}
        <div className="mb-8 grid grid-cols-3 gap-2.5">
          <Metric
            label={t('partnerDashboard.totalRegistrations', 'Total {{holdingNounPlural}}')}
            value={formatNumber(summary?.total_registrations ?? 0)}
            sub={`${summary?.registrations_this_month ?? 0} ${t('dashboard.thisMonth', 'this month')}`}
          />
          <Metric
            label={t('partnerDashboard.thisMonth', 'This Month')}
            value={formatNumber(summary?.registrations_this_month ?? 0)}
            sub={t('partnerDashboard.last30Days', 'last 30 days')}
          />
          <Metric
            label={t('dashboard.activeWarranties', 'Active {{warrantyNounPlural}}')}
            value={formatNumber(summary?.active_warranties ?? 0)}
          />
        </div>

        {/* ── Section 2: Registration Trend ───────────────── */}
        <div className="mb-4 rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] p-6">
          <p className="mb-4 text-[10px] font-[400] uppercase tracking-[0.14em] text-[var(--ink-3)]">
            {t('dashboard.registrationTrend', '{{holdingNoun}} Trend')}
            <span className="ml-2 text-[10px] normal-case tracking-normal text-[var(--ink-3)]">
              {t('partnerDashboard.last6Months', 'Last 6 months')}
            </span>
          </p>

          <div
            className="flex h-40 items-end gap-3 border-b-[0.5px] border-b-[var(--rule)] px-2"
          >
            {(data?.registrations_by_month ?? []).map((m) => {
              const height = monthlyMax > 0 ? (m.count / monthlyMax) * 100 : 0
              const barHeight = Math.max(height, 2)
              return (
                <div
                  key={m.month}
                  className="flex-1 flex flex-col items-center gap-1 group"
                >
                  <span
                    className="text-[10px] text-[var(--ink-3)] opacity-0 transition-opacity duration-150 group-hover:opacity-100"
                  >
                    {m.count}
                  </span>
                  <svg
                    viewBox="0 0 100 100"
                    preserveAspectRatio="none"
                    className="h-full w-full min-h-[2px]"
                    aria-hidden="true"
                  >
                    <rect
                      x="0"
                      y={100 - barHeight}
                      width="100"
                      height={barHeight}
                      rx="4"
                      fill={m.count > 0 ? 'var(--green)' : 'var(--rule)'}
                      opacity={m.count > 0 ? 0.65 : 0.3}
                      className="transition-all duration-500 group-hover:opacity-100"
                    />
                  </svg>
                </div>
              );
            })}
          </div>
          <div className="flex gap-3 px-2 mt-1.5">
            {(data?.registrations_by_month ?? []).map((m) => (
              <div key={m.month} className="flex-1 text-center">
                <span
                  className="text-[9px] uppercase tracking-[0.05em] text-[var(--ink-3)]"
                >
                  {monthLabel(m.month)}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* ── Section 3: By Product + Recent Registrations ── */}
        <div className="grid grid-cols-2 gap-4">
          {/* By Product */}
          <div className="rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] p-6">
            <ProductBarList items={data?.registrations_by_product ?? []} isLoading={isPending} isFetching={isFetching} />
          </div>

          {/* Recent Registrations */}
          <div className="rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] p-6">
            <div className="flex items-center justify-between mb-4">
              <p className="text-[10px] font-[400] uppercase tracking-[0.14em] text-[var(--ink-3)]">
                {t('dashboard.recentRegistrations', 'Recent {{holdingNounPlural}}')}
              </p>
              <button
                type="button"
                onClick={() => navigate('/registrations')}
                className="flex cursor-pointer items-center gap-1 border-none bg-transparent p-0 font-display text-[13px] text-[var(--ink-3)] transition-colors duration-150 hover:text-[var(--ink)]"
              >
                {t('common.viewAll', 'View all')}
                <ArrowRight className="h-3 w-3 ml-0.5" />
              </button>
            </div>

            {isPending || (isFetching && (data?.recent_registrations ?? []).length === 0) ? (
              <p className="text-[12px] text-[var(--ink-3)]">
                {t('common.loading', 'Loading...')}
              </p>
            ) : (data?.recent_registrations ?? []).length === 0 ? (
              <p className="text-[12px] text-[var(--ink-3)]">
                {t('dashboard.registrationsPlaceholder', '{{holdingNounPlural}} will appear here')}
              </p>
            ) : (
              <div className="flex flex-col">
                {data?.recent_registrations.map((reg, i) => (
                  <div
                    key={reg.uuid}
                    className={`flex cursor-pointer items-center justify-between py-2.5 transition-colors duration-100 hover:opacity-70 ${
                      i < (data?.recent_registrations.length ?? 1) - 1
                        ? 'border-b-[0.5px] border-b-[var(--rule)]'
                        : ''
                    }`}
                    onClick={() => navigate(`/registrations/${reg.uuid}`)}
                  >
                    <div className="min-w-0">
                      <p className="truncate text-[12px] text-[var(--ink)]">
                        {reg.product_name}
                      </p>
                      <p className="truncate text-[11px] text-[var(--ink-3)]">
                        {reg.consumer_email}
                      </p>
                    </div>
                    <span className="ml-3 shrink-0 text-[10px] text-[var(--ink-3)]">
                      {timeAgo(reg.created_at, locale)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </main>
    </AppShell>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function Metric({
  label,
  value,
  sub,
}: {
  label: string
  value: string
  sub?: string
}) {
  return (
    <div className="rounded-[8px] bg-[var(--bg-subtle)] px-[18px] py-4">
      <p className="mb-2 text-[10px] font-[400] uppercase tracking-[0.14em] text-[var(--ink-3)]">
        {label}
      </p>
      <p className="font-display text-[34px] leading-none text-[var(--ink)]">
        {value}
      </p>
      {sub && (
        <p className="mt-1 text-[11px] text-[var(--ink-3)]">
          {sub}
        </p>
      )}
    </div>
  );
}

function ProductBarList({ items, isLoading, isFetching }: { items: Array<{ name: string; count: number }>; isLoading?: boolean; isFetching?: boolean }) {
  const { t } = useTranslation()
  const max = Math.max(...items.map((i) => i.count), 1)
  return (
    <div>
      <p className="mb-4 text-[10px] font-[400] uppercase tracking-[0.14em] text-[var(--ink-3)]">
        {t('dashboard.byProduct', 'By Product')}
      </p>
      {isLoading || (isFetching && items.length === 0) ? (
        <p className="text-[12px] text-[var(--ink-3)]">
          {t('common.loading', 'Loading...')}
        </p>
      ) : items.length === 0 ? (
        <p className="text-[12px] text-[var(--ink-3)]">
          {t('partnerDashboard.productPlaceholder', 'Data will appear here')}
        </p>
      ) : (
        <div className="flex flex-col gap-3">
          {items.map((item) => (
            <div key={item.name}>
              <div className="flex items-center justify-between mb-1">
                <span className="truncate text-[12px] text-[var(--ink)]">
                  {item.name}
                </span>
                <span className="ml-2 shrink-0 text-[12px] text-[var(--ink-3)]">
                  {item.count}
                </span>
              </div>
              <div className="h-[2px] rounded-[1px] bg-[var(--rule)]">
                <svg
                  viewBox="0 0 100 2"
                  preserveAspectRatio="none"
                  className="h-full w-full"
                  aria-hidden="true"
                >
                  <rect
                    x="0"
                    y="0"
                    width={(item.count / max) * 100}
                    height="2"
                    rx="1"
                    fill="var(--green)"
                    className="transition-[width] duration-500 ease-in-out"
                  />
                </svg>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
